/**
 * AxlSupplyBreakdown — V2.K.6
 *
 * Renders a supply breakdown for AXL on the AxelarMonitor page.
 *
 * Why AXL is its own story :
 *  - **Uncapped** Cosmos-SDK chain with high baseline inflation
 *    (~11.5 % cumulated : 8 % base + 0.75 % per externally-verified
 *    chain). A governance proposal targets reducing the per-chain
 *    bonus to 0.3 %, which would bring total inflation down toward
 *    ~6.7 % at the current chain count.
 *  - **Cobalt upgrade (February 2025)** introduced an aggressive
 *    burn lever : **98 %** of cross-chain transaction fees are sent
 *    to a burn address, 2 % to the community grant pool. This is the
 *    deflationary mechanism — AXL becomes net deflationary whenever
 *    cumulative fee burn exceeds inflation mint over the period.
 *  - The original 1 B AXL allocation finishes vesting in 2026.
 *  - Circle acquired the Axelar team in 2024-2025 — added context for
 *    the trust + USDC-cross-chain narrative.
 */

import { useTranslation } from 'react-i18next';
import { useAxlSupply } from '../../hooks/useAxlSupply';

const KNOWN_ENDPOINTS: Array<{
  label: string;
  url: string;
  category: 'docs' | 'blog' | 'analysis';
  note: string;
}> = [
  {
    label: 'Cobalt Tokenomics Upgrade (blog officiel)',
    url: 'https://www.axelar.network/blog/cobalt-tokenomics-upgrade',
    category: 'blog',
    note: 'Annonce officielle Cobalt (fév. 2025) : 98 % des fees cross-chain burnés.',
  },
  {
    label: 'Introducing the AXL Token (blog officiel)',
    url: 'https://www.axelar.network/blog/introducing-the-axl-token',
    category: 'blog',
    note: 'Mécanique inflation 8 % base + 0,75 % par chaîne externe vérifiée.',
  },
  {
    label: 'Axelar Tokenomics — Figment',
    url: 'https://www.figment.io/insights/axelar-tokenomics/',
    category: 'analysis',
    note: 'Analyse indépendante du modèle inflation + rewards validators.',
  },
  {
    label: 'Axelar Gas Service Documentation',
    url: 'https://docs.axelar.dev/dev/gas-service/pricing/',
    category: 'docs',
    note: 'Pricing officiel des transactions cross-chain (source des fees burnés).',
  },
];

export function AxlSupplyBreakdown() {
  const { t, i18n } = useTranslation();
  const { data, isLoading, error } = useAxlSupply();

  const locale = i18n?.language || 'fr';
  const fmt = (n: number) =>
    n.toLocaleString(locale === 'fr' ? 'fr-FR' : locale, { maximumFractionDigits: 0 });
  const fmtB = (n: number) =>
    `${(n / 1_000_000_000).toLocaleString(locale === 'fr' ? 'fr-FR' : locale, {
      maximumFractionDigits: 2,
    })} Md`;

  return (
    <section
      aria-labelledby="axl-supply-breakdown-title"
      style={{
        background: 'rgba(0, 0, 0, 0.25)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        padding: '20px',
        marginBottom: '20px',
      }}
    >
      <header style={{ marginBottom: '14px' }}>
        <h2
          id="axl-supply-breakdown-title"
          style={{
            fontSize: '16px',
            fontWeight: 700,
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span aria-hidden style={{ color: '#1c2541' }}>⬢</span>
          {t('axl.supply.title', 'Supply AXL — répartition')}
        </h2>
        <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
          {t(
            'axl.supply.subtitle',
            'Chaîne Cosmos uncapped — inflation ~11,5 % pour les validators, mais Cobalt (fév. 2025) burn 98 % des fees cross-chain. Trajectoire visée : déflationniste quand le burn dépasse le mint.',
          )}
        </div>
      </header>

      {isLoading ? (
        <SkeletonBlock />
      ) : error || !data ? (
        <ErrorBlock />
      ) : (
        <>
          <StatGrid data={data} fmt={fmt} fmtB={fmtB} t={t} />
          <MintBurnVisual data={data} t={t} />
          <Legend t={t} />
          <CobaltNote t={t} fmt={fmt} data={data} />
          <KnownEndpoints t={t} />
        </>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Stat grid
// ─────────────────────────────────────────────────────────────────────

interface StatGridProps {
  data: NonNullable<ReturnType<typeof useAxlSupply>['data']>;
  fmt: (n: number) => string;
  fmtB: (n: number) => string;
  t: (key: string, fallback: string) => string;
}

function StatGrid({ data, fmt, fmtB, t }: StatGridProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '12px',
        marginBottom: '16px',
      }}
    >
      <StatCard
        label={t('axl.supply.circulating', 'En circulation')}
        value={fmtB(data.circulating)}
        sub={`${fmt(data.circulating)} AXL`}
        color="#1c2541"
      />
      <StatCard
        label={t('axl.supply.inflation', 'Inflation annuelle')}
        value={`${data.annualInflationPct.toFixed(2)} %`}
        sub={`~${fmtB(data.annualMint)} AXL mintés / an (validators)`}
        color="#f59e0b"
      />
      <StatCard
        label={t('axl.supply.feeBurn', 'Fee burn (Cobalt, fév. 2025)')}
        value={`${data.feeBurnPct} %`}
        sub={t('axl.supply.feeBurnNote', 'Des fees cross-chain envoyés à un burn address')}
        color="#10b981"
      />
      <StatCard
        label={t('axl.supply.cap', 'Cap maximal')}
        value="—"
        sub={t('axl.supply.capNote', 'Uncapped — inflation governance-tunable')}
        color="#6366f1"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: string;
}) {
  // #1c2541 is dark and would blend with the background; lift it.
  const bg = color === '#1c2541' ? 'rgba(28, 37, 65, 0.30)' : `${color}10`;
  const border = color === '#1c2541' ? 'rgba(80, 90, 130, 0.45)' : `${color}30`;
  return (
    <div
      style={{
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '20px',
          fontWeight: 700,
          color: 'var(--text)',
          marginTop: '4px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '11px',
          color: 'var(--muted)',
          marginTop: '2px',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Mint vs Burn visual — different from GRT : here we contrast a
// known inflation rate (mint) against an OPPORTUNISTIC burn (whose
// magnitude depends on cross-chain transaction volume, which we don't
// query in this scope). We surface the structural ratio: 98 % of fees
// burned, 11.5 % inflation — the user can read "burn pressure" qualitatively.
// ─────────────────────────────────────────────────────────────────────

function MintBurnVisual({
  data,
  t,
}: {
  data: NonNullable<ReturnType<typeof useAxlSupply>['data']>;
  t: (key: string, fallback: string) => string;
}) {
  return (
    <div
      style={{
        marginBottom: '8px',
        padding: '10px 12px',
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
      }}
      aria-label={t(
        'axl.supply.barAlt',
        'Comparaison qualitative : inflation mint vs taux de fee burn',
      )}
      role="img"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('axl.supply.mintSide', 'Mint : inflation validators')}
          </div>
          <div
            style={{
              height: '14px',
              borderRadius: '6px',
              background: `linear-gradient(90deg, #f59e0b ${data.annualInflationPct}%, rgba(245, 158, 11, 0.15) ${data.annualInflationPct}%)`,
              position: 'relative',
            }}
          >
            <span
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '10px',
                fontWeight: 700,
                color: 'var(--text)',
              }}
            >
              +{data.annualInflationPct.toFixed(1)} %
            </span>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: '160px' }}>
          <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '4px' }}>
            {t('axl.supply.burnSide', 'Burn : 98 % des fees cross-chain')}
          </div>
          <div
            style={{
              height: '14px',
              borderRadius: '6px',
              background: `linear-gradient(90deg, #10b981 ${data.feeBurnPct}%, rgba(16, 185, 129, 0.15) ${data.feeBurnPct}%)`,
              position: 'relative',
            }}
          >
            <span
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '10px',
                fontWeight: 700,
                color: 'white',
              }}
            >
              −{data.feeBurnPct} %
            </span>
          </div>
        </div>
      </div>
      <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.5 }}>
        {t(
          'axl.supply.visualNote',
          'Ratio structurel — le mint est constant (inflation), le burn dépend du volume de transactions cross-chain. AXL devient déflationniste quand le burn cumulé dépasse le mint sur la période.',
        )}
      </div>
    </div>
  );
}

function Legend({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: '14px',
        flexWrap: 'wrap',
        fontSize: '11px',
        color: 'var(--muted)',
        marginBottom: '14px',
      }}
    >
      <LegendItem color="#f59e0b" label={t('axl.supply.legendMint', 'Inflation — 8 % base + 0,75 % par chaîne externe vérifiée')} />
      <LegendItem color="#10b981" label={t('axl.supply.legendBurn', 'Fee burn — 98 % des fees cross-chain (Cobalt fév. 2025)')} />
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      <span
        aria-hidden
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '3px',
          background: color,
          display: 'inline-block',
        }}
      />
      {label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Cobalt callout
// ─────────────────────────────────────────────────────────────────────

function CobaltNote({
  t,
  fmt,
  data,
}: {
  t: (key: string, fallback: string) => string;
  fmt: (n: number) => string;
  data: NonNullable<ReturnType<typeof useAxlSupply>['data']>;
}) {
  const body = t(
    'axl.supply.cobaltBody',
    "L'upgrade Cobalt (février 2025) a transformé la tokenomique d'Axelar en redirigeant {{burn}} % des fees de transactions cross-chain vers une adresse de burn (au lieu de revenir aux tokenholders). Les 2 % restants alimentent le pool de community grants. À l'inflation actuelle ({{inflation}} %), ~{{mint}} AXL sont mintés par an pour rémunérer les validators. AXL devient net déflationniste si le volume cross-chain génère suffisamment de fees pour que le burn cumulé dépasse ce mint. Une proposition de gouvernance vise à réduire l'inflation par chaîne externe de 0,75 % à 0,3 %, ce qui ramènerait le taux cumulé vers ~6,7 %. La supply initiale de 1 Md AXL termine son vesting en 2026.",
  )
    .replace('{{burn}}', String(data.feeBurnPct))
    .replace('{{inflation}}', data.annualInflationPct.toFixed(2))
    .replace('{{mint}}', fmt(data.annualMint));

  return (
    <div
      style={{
        padding: '12px 14px',
        background: 'rgba(28, 37, 65, 0.20)',
        border: '1px dashed rgba(80, 90, 130, 0.45)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--text)',
        lineHeight: 1.6,
        marginBottom: '14px',
      }}
    >
      <strong style={{ color: '#e5e7eb' }}>
        {t('axl.supply.cobaltTitle', 'Cobalt — fee burn 98 % (févr. 2025)')}
      </strong>
      <div style={{ marginTop: '4px' }}>{body}</div>
      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
        {t(
          'axl.supply.cobaltSource',
          'Sources : axelar.network/blog/cobalt-tokenomics-upgrade, figment.io/insights/axelar-tokenomics.',
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Known endpoints
// ─────────────────────────────────────────────────────────────────────

function KnownEndpoints({ t }: { t: (key: string, fallback: string) => string }) {
  return (
    <details
      style={{
        background: 'rgba(255, 255, 255, 0.02)',
        border: '1px solid var(--border)',
        borderRadius: '10px',
        padding: '12px 14px',
      }}
    >
      <summary
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'var(--text)',
          cursor: 'pointer',
          listStyle: 'revert',
        }}
      >
        {t('axl.supply.knownEndpointsTitle', 'Sources officielles & analyse')}
      </summary>

      <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px', lineHeight: 1.6 }}>
        {t(
          'axl.supply.knownEndpointsDisclaimer',
          'L\'adresse du burn account introduite par Cobalt n\'est pas publiée dans le blog officiel — le mécanisme est implémenté côté protocole via le fee collector account de l\'Axelar Foundation. Aucune adresse hardcodée pour éviter toute désinformation.',
        )}
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {KNOWN_ENDPOINTS.map((entry) => (
          <li
            key={entry.url}
            style={{
              padding: '10px 0',
              borderTop: '1px solid var(--border)',
              fontSize: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
              <CategoryPill category={entry.category} />
              <a
                href={entry.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}
              >
                {entry.label} ↗
              </a>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
              {entry.note}
            </div>
          </li>
        ))}
      </ul>
    </details>
  );
}

function CategoryPill({ category }: { category: 'docs' | 'blog' | 'analysis' }) {
  const styles: Record<typeof category, { bg: string; color: string; label: string }> = {
    blog: { bg: 'rgba(28, 37, 65, 0.40)', color: '#cbd5e1', label: 'Blog' },
    docs: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', label: 'Docs' },
    analysis: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', label: 'Analyse' },
  };
  const s = styles[category];
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 700,
        padding: '2px 6px',
        borderRadius: '4px',
        background: s.bg,
        color: s.color,
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
      }}
    >
      {s.label}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Loading / error states
// ─────────────────────────────────────────────────────────────────────

function SkeletonBlock() {
  return (
    <div aria-hidden>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              background: 'rgba(255, 255, 255, 0.04)',
              borderRadius: '10px',
              height: '72px',
            }}
          />
        ))}
      </div>
      <div
        style={{
          height: '24px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: '8px',
          marginBottom: '14px',
        }}
      />
    </div>
  );
}

function ErrorBlock() {
  return (
    <div
      style={{
        padding: '14px',
        background: 'rgba(239, 68, 68, 0.08)',
        border: '1px solid rgba(239, 68, 68, 0.25)',
        borderRadius: '10px',
        fontSize: '12px',
        color: 'var(--muted)',
      }}
    >
      Données CoinGecko indisponibles pour le moment. La supply AXL sera affichée dès que l'API répondra.
    </div>
  );
}
